import React, { useEffect, useState } from "react";
import api, { formatErr } from "../lib/api";
import { Plus, Mail, Phone, Pencil, Trash2, X, Search, IdCard, KeyRound, Copy, Eye, EyeOff, Hash } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { COURSES, BATCHES, withCurrent } from "../lib/constants";

const EMPTY = { name: "", email: "", password: "", roll_number: "", registration_number: "", batch: "", degree: "", phone: "" };

export default function Students() {
  const { user } = useAuth();
  const isManager = user?.role === "manager";
  const [data, setData] = useState({ items: [], filters: { batches: [], degrees: [] } });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ batch: "", degree: "", q: "" });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [resetShown, setResetShown] = useState(null);

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (filters.batch) p.set("batch", filters.batch);
    if (filters.degree) p.set("degree", filters.degree);
    if (filters.q) p.set("q", filters.q);
    api.get(`/students-filtered${p.toString() ? `?${p}` : ""}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [filters.batch, filters.degree, filters.q]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || "",
      email: s.email,
      password: "",
      roll_number: s.roll_number || "",
      registration_number: s.registration_number || "",
      batch: s.batch || "",
      degree: s.degree || "",
      phone: s.phone || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const { name, roll_number, registration_number, batch, degree, phone } = form;
        await api.patch(`/students/${editing.id}`, { name, roll_number, registration_number, batch, degree, phone });
      } else {
        await api.post("/students", form);
      }
      toast.success(editing ? "Student updated" : "Student added");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name}?`)) return;
    try { await api.delete(`/students/${s.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const doResetPassword = async () => {
    if (!resetPw || resetPw.length < 6) { toast.error("Password must be at least 6 chars"); return; }
    try {
      const r = await api.post(`/users/${resetTarget.id}/reset-password`, { new_password: resetPw });
      setResetShown(r.data);
      setResetTarget(null);
      setResetPw("");
    } catch (e) { toast.error(formatErr(e)); }
  };

  function genPw() {
    const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
    let s = "";
    for (let i = 0; i < 10; i++) s += chars[Math.floor(Math.random() * chars.length)];
    return s;
  }

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-brand-muted font-semibold">Team</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-1">Students</h1>
          <p className="text-brand-muted mt-1 text-sm">Filter by batch & degree</p>
        </div>
        <button onClick={openCreate} className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white font-medium" data-testid="add-student-btn">
          <Plus className="h-4 w-4" /> Add Student
        </button>
      </div>

      {/* filters */}
      <div className="bg-white border border-brand-border rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-brand-muted">Search</label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted" />
              <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Name / email / roll" className="w-full h-10 pl-9 pr-3 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none text-sm" data-testid="filter-q" />
            </div>
          </div>
          <SelectInput label="Batch" value={filters.batch} onChange={(v) => setFilters({ ...filters, batch: v })} options={data.filters.batches} testId="filter-batch" />
          <SelectInput label="Degree" value={filters.degree} onChange={(v) => setFilters({ ...filters, degree: v })} options={data.filters.degrees} testId="filter-degree" />
        </div>
      </div>

      {loading ? <div className="text-brand-muted">Loading…</div> : data.items.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-brand-border bg-white p-12 text-center text-brand-muted">No students match the filters</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.items.map((s) => (
            <div key={s.id} className="bg-white border border-brand-border rounded-lg p-5" data-testid={`student-${s.email}`}>
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-full bg-brand-primary/10 text-brand-primary grid place-items-center font-heading font-semibold">{s.name?.charAt(0)?.toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-semibold truncate">{s.name}</div>
                  <div className="text-xs text-brand-muted truncate inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {s.email}</div>
                </div>
                <div className="flex items-center gap-1">
                  {isManager && (
                    <button onClick={() => setResetTarget(s)} className="h-8 w-8 rounded-md hover:bg-brand-surfaceAlt grid place-items-center" title="Reset password" data-testid={`reset-pw-${s.email}`}><KeyRound className="h-3.5 w-3.5 text-brand-primary" /></button>
                  )}
                  <button onClick={() => openEdit(s)} className="h-8 w-8 rounded-md hover:bg-brand-surfaceAlt grid place-items-center" data-testid={`edit-student-${s.email}`}><Pencil className="h-3.5 w-3.5 text-brand-muted" /></button>
                  <button onClick={() => remove(s)} className="h-8 w-8 rounded-md hover:bg-brand-surfaceAlt grid place-items-center" data-testid={`delete-student-${s.email}`}><Trash2 className="h-3.5 w-3.5 text-brand-danger" /></button>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                {s.roll_number && <KV k="Roll" v={s.roll_number} icon={IdCard} />}
                {s.registration_number && <KV k="Reg #" v={s.registration_number} icon={Hash} />}
                {s.batch && <KV k="Batch" v={s.batch} />}
                {s.degree && <KV k="Course" v={s.degree} block />}
                {s.phone && <KV k="Phone" v={s.phone} icon={Phone} block />}
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-brand-border w-full max-w-xl p-6" data-testid="student-modal">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-semibold">{editing ? "Edit Student" : "Add Student"}</h2>
              <button type="button" onClick={() => setOpen(false)} className="h-9 w-9 rounded-md hover:bg-brand-surfaceAlt grid place-items-center"><X className="h-4 w-4" /></button>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Full Name *"><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="s-name" /></Field>
              <Field label="Email *"><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} data-testid="s-email" /></Field>
              {!editing && (
                <Field label="Temporary Password *">
                  <div className="relative">
                    <input type={showPw ? "text" : "password"} required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full h-11 px-3 pr-12 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none" data-testid="s-password" />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 grid place-items-center rounded hover:bg-brand-border/50">
                      {showPw ? <EyeOff className="h-4 w-4 text-brand-muted" /> : <Eye className="h-4 w-4 text-brand-muted" />}
                    </button>
                  </div>
                </Field>
              )}
              <Field label="Roll Number"><Input value={form.roll_number} onChange={(e) => setForm({ ...form, roll_number: e.target.value })} data-testid="s-roll" /></Field>
              <Field label="Registration Number"><Input value={form.registration_number} onChange={(e) => setForm({ ...form, registration_number: e.target.value })} placeholder="ACN-2024-001" data-testid="s-regno" /></Field>
              <Field label="Course">
                <select value={form.degree} onChange={(e) => setForm({ ...form, degree: e.target.value })} className="w-full h-11 px-3 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none" data-testid="s-degree">
                  <option value="">Select course…</option>
                  {withCurrent(COURSES, form.degree).map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </Field>
              <Field label="Batch (Joining Year)">
                <select value={form.batch} onChange={(e) => setForm({ ...form, batch: e.target.value })} className="w-full h-11 px-3 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none" data-testid="s-batch">
                  <option value="">Select joining year…</option>
                  {withCurrent(BATCHES, form.batch).map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </Field>
              <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="s-phone" /></Field>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="h-11 px-4 rounded-md border border-brand-border bg-white hover:bg-brand-surfaceAlt">Cancel</button>
              <button type="submit" disabled={saving} className="h-11 px-6 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white font-medium disabled:opacity-60" data-testid="s-submit">
                {saving ? "Saving…" : editing ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}
      {/* Reset password modal */}
      {resetTarget && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setResetTarget(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-brand-border w-full max-w-md p-6">
            <h2 className="font-heading text-xl font-semibold">Reset password</h2>
            <p className="text-brand-muted text-sm mt-1">Set a new password for <b>{resetTarget.name}</b> ({resetTarget.email})</p>
            <div className="mt-4">
              <div className="relative">
                <input type={showPw ? "text" : "password"} minLength={6} value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="New password (min 6 chars)" className="w-full h-11 px-3 pr-12 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none" data-testid="s-reset-pw-input" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 grid place-items-center rounded hover:bg-brand-border/50">
                  {showPw ? <EyeOff className="h-4 w-4 text-brand-muted" /> : <Eye className="h-4 w-4 text-brand-muted" />}
                </button>
              </div>
              <button onClick={() => setResetPw(genPw())} className="text-xs text-brand-primary font-medium mt-2">Generate strong password</button>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setResetTarget(null)} className="h-11 px-4 rounded-md border border-brand-border bg-white hover:bg-brand-surfaceAlt">Cancel</button>
              <button onClick={doResetPassword} className="h-11 px-6 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white font-medium" data-testid="s-reset-confirm">Reset</button>
            </div>
          </div>
        </div>
      )}

      {resetShown && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setResetShown(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-brand-border w-full max-w-md p-6">
            <h2 className="font-heading text-xl font-semibold">Password updated</h2>
            <p className="text-brand-muted text-sm mt-1">Share these credentials with the student. Shown only once.</p>
            <div className="mt-4 rounded-md border border-brand-border bg-brand-surfaceAlt p-4 text-sm font-mono">
              <div><b className="font-semibold">Email:</b> {resetShown.email}</div>
              <div className="flex items-center gap-2 mt-1">
                <span><b className="font-semibold">Password:</b> {resetShown.new_password}</span>
                <button onClick={() => { navigator.clipboard.writeText(resetShown.new_password); toast.success("Copied"); }} className="inline-flex items-center gap-1 text-brand-primary text-xs font-medium"><Copy className="h-3 w-3" /> copy</button>
              </div>
            </div>
            <button onClick={() => setResetShown(null)} className="mt-4 w-full h-11 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white font-medium">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return <div className={className}><label className="text-xs font-semibold uppercase tracking-wider text-brand-muted">{label}</label><div className="mt-1.5">{children}</div></div>;
}
function Input(props) { return <input {...props} className="w-full h-11 px-3 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none disabled:opacity-60" />; }
function SelectInput({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-wider text-brand-muted">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full h-10 px-2 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none text-sm" data-testid={testId}>
        <option value="">All</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
function KV({ k, v, block, icon: Icon }) {
  return (
    <div className={block ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-brand-muted font-semibold">{k}</div>
      <div className="font-medium inline-flex items-center gap-1.5">{Icon && <Icon className="h-3.5 w-3.5 text-brand-muted" />} {v}</div>
    </div>
  );
}
